import React, { useEffect, useState } from "react";
import { Trash2, Plus, Pencil, Check } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import AdminLayout from "./AdminLayout";
import api from "../../api";
import { useToast } from "../../hooks/use-toast";

// Reusable Add / Delete / Edit manager backed by the /api/results collection.
// Used by "Manage Result" and "Previous Result".
const ResultManager = ({ title }) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [list, setList] = useState([]);
  const [date, setDate] = useState("");
  const [fr, setFr] = useState("");
  const [sr, setSr] = useState("");
  const [editId, setEditId] = useState(null);

  const load = () => api.getResults().then(setList).catch(() => {});
  useEffect(() => {
    load();
  }, []);

  const invalidatePublic = () => {
    queryClient.invalidateQueries({ queryKey: ["results"] });
    queryClient.invalidateQueries({ queryKey: ["analytics"] });
    queryClient.invalidateQueries({ queryKey: ["predict"] });
  };

  const toDisplay = (iso) => {
    if (!iso) return "";
    const [y, m, d] = iso.split("-");
    return `${d}-${m}-${y}`;
  };

  const reset = () => {
    setEditId(null);
    setDate("");
    setFr("");
    setSr("");
  };

  const addOrUpdate = async (e) => {
    e.preventDefault();
    if (!date || !fr) {
      toast({ title: "Please enter date and F/R number", variant: "destructive" });
      return;
    }
    const payload = { date: toDisplay(date), fr, sr };
    try {
      if (editId) {
        await api.updateResult(editId, payload);
        toast({ title: "Result updated" });
      } else {
        await api.addResult(payload);
        toast({ title: "Result added" });
      }
      reset();
      await load();
      invalidatePublic();
    } catch (err) {
      toast({ title: "Something went wrong", variant: "destructive" });
    }
  };

  const del = async (id) => {
    try {
      await api.deleteResult(id);
      toast({ title: "Result deleted" });
      if (editId === id) reset();
      await load();
      invalidatePublic();
    } catch (err) {
      toast({ title: "Delete failed", variant: "destructive" });
    }
  };

  const startEdit = (r) => {
    setEditId(r.id);
    const [d, m, y] = r.date.split("-");
    setDate(`${y}-${m}-${d}`);
    setFr(r.fr);
    setSr(r.sr);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <AdminLayout title={title} showBack>
      <div className="bg-white border-2 border-dashed border-[#7fd0b8] rounded-lg p-5 mb-6">
        <form onSubmit={addOrUpdate} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[#5b6b7b] mb-1">
              Date:
            </label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full max-w-xs border border-gray-300 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#4a7fb5]"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#5b6b7b] mb-1">
              F/R (First Round):
            </label>
            <input
              value={fr}
              onChange={(e) => setFr(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#4a7fb5]"
              placeholder="e.g. 22"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#5b6b7b] mb-1">
              S/R (Second Round):
            </label>
            <input
              value={sr}
              onChange={(e) => setSr(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#4a7fb5]"
              placeholder="e.g. 63"
            />
          </div>
          <button
            type="submit"
            className="inline-flex items-center gap-2 bg-[#4a7fb5] hover:bg-[#3d6c9c] text-white font-medium px-6 py-2.5 rounded-md transition-colors"
          >
            {editId ? <Check className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
            {editId ? "Update" : "Submit"}
          </button>
          {editId && (
            <button
              type="button"
              onClick={reset}
              className="ml-2 text-sm text-gray-500 hover:underline"
            >
              Cancel
            </button>
          )}
        </form>
      </div>

      <div className="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm">
        <table className="w-full text-center text-sm">
          <thead>
            <tr className="bg-[#f1f5f9] text-[#5b6b7b]">
              <th className="py-2.5 font-semibold">Sl.No</th>
              <th className="py-2.5 font-semibold">Date</th>
              <th className="py-2.5 font-semibold">First Round</th>
              <th className="py-2.5 font-semibold">Second Round</th>
              <th className="py-2.5 font-semibold">Action</th>
            </tr>
          </thead>
          <tbody>
            {list.map((r, i) => (
              <tr key={r.id} className={i % 2 ? "bg-gray-50" : "bg-white"}>
                <td className="py-2 border-t border-gray-200">{i + 1}</td>
                <td className="py-2 border-t border-gray-200">{r.date}</td>
                <td className="py-2 border-t border-gray-200 font-semibold">{r.fr}</td>
                <td className="py-2 border-t border-gray-200 font-semibold">{r.sr}</td>
                <td className="py-2 border-t border-gray-200">
                  <div className="flex items-center justify-center gap-1.5">
                    <button
                      onClick={() => startEdit(r)}
                      className="inline-flex items-center gap-1 bg-[#4a7fb5] hover:bg-[#3d6c9c] text-white text-xs px-2.5 py-1.5 rounded transition-colors"
                    >
                      <Pencil className="w-3 h-3" /> Edit
                    </button>
                    <button
                      onClick={() => del(r.id)}
                      className="inline-flex items-center gap-1 bg-[#e5342a] hover:bg-[#c92c23] text-white text-xs px-2.5 py-1.5 rounded transition-colors"
                    >
                      <Trash2 className="w-3 h-3" /> Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {list.length === 0 && (
              <tr>
                <td colSpan={5} className="py-6 text-gray-400 border-t">
                  No results yet. Add one above.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </AdminLayout>
  );
};

export default ResultManager;
